import { isArray } from 'lodash';
import { RSAA } from 'redux-api-middleware';
import { Dispatch } from 'redux';
import { TGetRootState, TAsyncDispatchResult } from 'types/shared';
import { IDSPListProps, TDispatchResult } from 'types/shared';

const GET_REQUEST = 'OPUS/CUSTOM_DSP_LISTS/GET_REQUEST';
const PUT_REQUEST = 'OPUS/CUSTOM_DSP_LISTS/PUT_REQUEST';
const GET_SUCCESS = 'OPUS/CUSTOM_DSP_LISTS/GET_SUCCESS';
const PUT_SUCCESS = 'OPUS/CUSTOM_DSP_LISTS/PUT_SUCCESS';
const FAILURE = 'OPUS/CUSTOM_DSP_LISTS/FAILURE';

type SelectedCustomDSPListsActionTypes =
  | typeof GET_REQUEST
  | typeof PUT_REQUEST
  | typeof GET_SUCCESS
  | typeof PUT_SUCCESS
  | typeof FAILURE;

interface ISelectedCustomDSPListsAction {
  type: SelectedCustomDSPListsActionTypes;
  error?: boolean;
  payload: any;
}
export interface ICustomDSPListsState {
  pending: boolean;
  fulfilled: boolean;
  error: boolean;
  rejected: boolean;
  reason: any;
  data: [] | Record<string, IDSPListProps>;
}

const initialState: ICustomDSPListsState = {
  pending: false,
  fulfilled: false,
  error: false,
  rejected: false,
  reason: null,
  data: [],
};

export default function reducer(
  state = initialState,
  action: ISelectedCustomDSPListsAction
): ICustomDSPListsState {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
      };
    case PUT_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case PUT_SUCCESS:
      // We do not update the on put success because we are updating optimistically
      // and the user might already have newer settings once the success comes in.
      // If the PUT call fails the user will see an error message in the DSP selector
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
      };
    case GET_SUCCESS:
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: Boolean(action.error),
        reason: action.payload,
      };

    default:
      return state;
  }
}

export function saveCustomDSPList(
  newDSPList: any | any[],
  key?: string
): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const { customDSPLists } = getState();
    const current = customDSPLists.data;

    const payload = key
      ? { ...current, [key]: { ...newDSPList } }
      : { ...current, [newDSPList.id]: { ...newDSPList } };

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_custom_dsp_lists`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

export function removeCustomDSPList(DSPListId?: string): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const { customDSPLists } = getState();
    const current = customDSPLists.data;

    if (DSPListId && !isArray(current) && current[DSPListId as any]) {
      delete current[DSPListId as any];
    }
    const payload = current;

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_custom_dsp_lists`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

export function loadCustomDSPLists(): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    // get the user id
    const { id } = getState().authentication.profile;
    const { customDSPLists } = getState();

    // fetch the users' custom DSP Lists
    return await dispatch({
      [RSAA]: {
        bailout: customDSPLists.pending || customDSPLists.fulfilled,
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: async function payload(
              action: ISelectedCustomDSPListsAction,
              state: ICustomDSPListsState,
              res: any
            ): Promise<any> {
              const customDSPLists = await res.json();

              return customDSPLists;
            },
          },
          {
            type: FAILURE,
            meta: (_action, _state, res: any): void => {
              if (res?.status === 404) {
                loadDefaultsIf404()(dispatch, getState);
              }
            },
          },
        ],
        endpoint: `/kv/user_${id}_custom_dsp_lists`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };
}

const loadDefaultsIf404 =
  (): TAsyncDispatchResult =>
  (dispatch: Dispatch, getState: TGetRootState): any => {
    // get the user id
    const { id } = getState().authentication.profile;

    // fetch the users' custom regions
    return dispatch({
      [RSAA]: {
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: {},
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_custom_dsp_lists`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };

export const shouldLoadCustomDSPLists = (): TDispatchResult<boolean> => {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const state = getState();
    const { customDSPLists } = state;
    const { pending, data, fulfilled } = customDSPLists;
    return (!data && !pending) || (!pending && !fulfilled);
  };
};
